/* Temple Mount Soil — Cart Drawer
 * Vanilla web component. Wraps Shopify's AJAX Cart API.
 *
 * Public events:
 *   - 'cart:open'        → opens drawer
 *   - 'cart:close'       → closes drawer
 *   - 'cart:refresh'     → re-fetches /cart.js and re-renders
 *
 * Public DOM API (via document.querySelector('tms-cart-drawer')):
 *   - .open(), .close(), .toggle()
 *   - .refresh()
 *
 * Form interception:
 *   Any <form action="/cart/add"> on the page is intercepted, items are added
 *   via fetch to /cart/add.js, then the drawer opens with the fresh cart.
 */

(function () {
  if (customElements.get('tms-cart-drawer')) return;

  const ROUTES = (window.routes && window.routes.cart_url) ? window.routes : {
    cart_url: '/cart',
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_update_url: '/cart/update',
  };

  const fmtMoney = (cents) => {
    if (window.Shopify && window.Shopify.formatMoney && window.cartDrawerSettings) {
      return window.Shopify.formatMoney(cents, window.cartDrawerSettings.moneyFormat);
    }
    // Fallback: USD with two decimals
    const sign = cents < 0 ? '-' : '';
    return sign + '$' + (Math.abs(cents) / 100).toFixed(2);
  };

  // Compact escape — line item titles can contain special chars
  const escapeHtml = (s) =>
    String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._onKey = this._onKey.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
    }

    connectedCallback() {
      // Bind UI events scoped to the drawer
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-cd-action]');
        if (!t) return;
        e.preventDefault();
        const action = t.dataset.cdAction;
        const key = t.dataset.cdKey;

        if (action === 'close') return this.close();
        if (action === 'inc') return this._changeQtyByKey(key, +1);
        if (action === 'dec') return this._changeQtyByKey(key, -1);
        if (action === 'remove') return this._setQtyByKey(key, 0);
        if (action === 'checkout') return (window.location.href = '/checkout');
      });

      // Global wiring (capture phase for click + submit so we beat the theme's
      // own handlers that may stopPropagation in bubble phase).
      document.addEventListener('keydown', this._onKey);
      document.addEventListener('click', this._onDocClick, true);
      document.addEventListener('submit', this._onSubmit, true);

      // Custom events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:close', () => this.close());
      document.addEventListener('cart:refresh', () => this.refresh());

      // Theme editor: auto-open when this section is selected so merchants can preview it
      if (window.Shopify && window.Shopify.designMode) {
        const isOurs = (target) => target && (
          target === this ||
          (target.querySelector && target.querySelector('tms-cart-drawer'))
        );
        document.addEventListener('shopify:section:select',  (e) => isOurs(e.target) && this.open());
        document.addEventListener('shopify:section:load',    (e) => isOurs(e.target) && this.open());
        document.addEventListener('shopify:section:deselect',(e) => isOurs(e.target) && this.close());
        document.addEventListener('shopify:section:unload',  (e) => isOurs(e.target) && this.close());
      }

      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      document.removeEventListener('click', this._onDocClick, true);
      document.removeEventListener('submit', this._onSubmit, true);
    }

    _onKey(e) {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
    }

    _onDocClick(e) {
      // Highest priority: any element wired up via aria-controls="cart-drawer"
      // (the existing TMS header cart icon uses this). Capture-phase listener
      // means we beat the theme's own toggle handler.
      const ariaTrigger = e.target.closest('[aria-controls="cart-drawer"]');
      if (ariaTrigger) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open();
        return;
      }
      // Explicit opt-in trigger
      const opener = e.target.closest('[data-cd-open]');
      if (opener) {
        e.preventDefault();
        this.open();
        return;
      }
      // Auto-trigger: any anchor pointing to /cart. Opt out per-link with data-cd-skip.
      const link = e.target.closest('a[href]');
      if (link && !link.hasAttribute('data-cd-skip')) {
        const href = link.getAttribute('href') || '';
        const cartUrl = ROUTES.cart_url || '/cart';
        const isCartLink =
          href === cartUrl || href === cartUrl + '/' ||
          href.startsWith(cartUrl + '?') || href.startsWith(cartUrl + '#') ||
          /(^|\/)cart(\/|\?|#|$)/.test(href);
        if (isCartLink) {
          e.preventDefault();
          this.open();
          return;
        }
      }
      // Close on overlay click
      if (e.target === this.querySelector('.cd-overlay')) this.close();
    }

    async _onSubmit(e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = (form.getAttribute('action') || '').split('?')[0];
      if (!/\/cart\/add$/.test(action)) return;
      // Skip forms that explicitly opt out
      if (form.hasAttribute('data-cd-skip')) return;

      e.preventDefault();
      // Open immediately with a busy state — the content reconciles when
      // the add + refresh complete. Perceived latency drops to ~0 even
      // though the network cost is unchanged.
      this.open();
      const drawerPane = this.querySelector('.cd-drawer');
      if (drawerPane) drawerPane.setAttribute('aria-busy', 'true');
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

      try {
        const fd = new FormData(form);
        const res = await fetch(`${ROUTES.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add to cart');
        }
        await this.refresh();
        this.open();
      } catch (err) {
        // Surface the error inline; let the page handle its own toasts if any
        console.warn('[cart-drawer] add failed:', err);
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: err.message } }));
      } finally {
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
        const dp = this.querySelector('.cd-drawer');
        if (dp) dp.removeAttribute('aria-busy');
      }
    }

    async _setQtyByKey(key, qty) {
      const row = this.querySelector(`.cd-item[data-cd-key="${CSS.escape(key)}"]`);
      if (row) row.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${ROUTES.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        if (!res.ok) throw new Error('change failed');
        // /cart/change.js already returns the FULL cart — render it directly
        // instead of paying for a second /cart.js round-trip via refresh().
        const cart = await res.json();
        this._render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (err) {
        console.warn('[cart-drawer] change failed:', err);
        if (row) row.removeAttribute('aria-busy');
      }
    }

    async _changeQtyByKey(key, delta) {
      const row = this.querySelector(`.cd-item[data-cd-key="${CSS.escape(key)}"]`);
      if (!row) return;
      const valEl = row.querySelector('.cd-qty__value');
      const current = parseInt(valEl?.textContent || '0', 10);
      const next = Math.max(0, current + delta);
      return this._setQtyByKey(key, next);
    }

    async refresh() {
      try {
        const res = await fetch(`${ROUTES.cart_url}.js`, {
          headers: { 'Accept': 'application/json' },
        });
        const cart = await res.json();
        this._render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (err) {
        console.warn('[cart-drawer] refresh failed:', err);
      }
    }

    open() {
      this.setAttribute('open', '');
      document.documentElement.classList.add('cd-locked');
      document.body.classList.add('cd-locked');
      // Move focus to close button for a11y
      const closeBtn = this.querySelector('[data-cd-action="close"]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('cd-locked');
      document.body.classList.remove('cd-locked');
    }

    toggle() { this.hasAttribute('open') ? this.close() : this.open(); }

    _render(cart) {
      const drawer = this.querySelector('.cd-drawer');
      if (!drawer) return;

      const itemCount = cart.item_count || 0;
      const countEl = this.querySelector('[data-cd-count]');
      if (countEl) countEl.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;

      const bodyEl = this.querySelector('.cd-body');
      if (!bodyEl) return;

      if (itemCount === 0) {
        bodyEl.innerHTML = this._emptyHtml();
        const footer = this.querySelector('.cd-footer');
        if (footer) footer.hidden = true;
        return;
      }

      // Render items + promo
      const promoHtml = this._promoHtml(cart);
      const itemsHtml = cart.items.map((it) => this._itemHtml(it)).join('');
      bodyEl.innerHTML = `${promoHtml}<div class="cd-items">${itemsHtml}</div>`;

      // Footer totals
      const footer = this.querySelector('.cd-footer');
      if (footer) footer.hidden = false;
      const strikeEl = this.querySelector('[data-cd-original]');
      const totalEl = this.querySelector('[data-cd-subtotal]');
      const ctaTotalEl = this.querySelector('[data-cd-cta-subtotal]');

      // Original total = sum of original_line_price across items. cart.items_subtotal_price
      // is post line-level discounts and can equal total_price when items are individually
      // marked free (e.g. BOGO at 100%), so it's not a reliable "before discount" total.
      const original = (cart.items || []).reduce(
        (sum, it) => sum + (it.original_line_price ?? it.line_price ?? 0), 0
      );
      const subtotal = cart.total_price;
      const hasDiscount = original > subtotal;

      if (totalEl) totalEl.textContent = fmtMoney(subtotal);
      if (ctaTotalEl) ctaTotalEl.textContent = fmtMoney(subtotal);
      if (strikeEl) {
        if (hasDiscount && original != null && original > subtotal) {
          strikeEl.hidden = false;
          strikeEl.textContent = fmtMoney(original);
        } else {
          strikeEl.hidden = true;
        }
      }
    }

    _emptyHtml() {
      const settings = window.cartDrawerSettings || {};
      const continueUrl = settings.continueShoppingUrl || '/collections/all';
      return `
        <div class="cd-empty">
          <div class="cd-empty__icon" aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>
              <path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          </div>
          <div class="cd-empty__title">${escapeHtml(settings.emptyTitle || 'Your cart awaits')}</div>
          <div class="cd-empty__sub">${escapeHtml(settings.emptySubtitle || 'Begin your journey as a custodian of a two-thousand-year-old story.')}</div>
          <a class="cd-empty__cta" href="${escapeHtml(continueUrl)}">${escapeHtml(settings.emptyCta || 'Explore the Collection')}</a>
        </div>
      `;
    }

    _promoHtml(cart) {
      const s = window.cartDrawerSettings || {};
      if (!s.promoEnabled) return '';
      const unlocked = !!(cart && cart.items && cart.items.some(it =>
        (it.original_line_price ?? 0) > (it.final_line_price ?? it.line_price ?? 0)
      ));
      const title = unlocked
        ? (s.promoTitleUnlocked || 'Discount Applied')
        : (s.promoTitle || 'Up to 20% Off Selected Keepsakes');
      const sub = unlocked
        ? (s.promoSubUnlocked || 'Your savings have been applied at checkout.')
        : (s.promoSub || 'Discounts apply automatically at checkout.');
      const icon = unlocked
        ? `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
             <path d="M20 6 9 17l-5-5"/>
           </svg>`
        : `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
             <path d="M11 3H4v7l11 11 7-7L11 3Z"/><circle cx="8" cy="7" r="1.2"/>
           </svg>`;
      return `
        <div class="cd-promo${unlocked ? ' cd-promo--unlocked' : ''}" data-cd-promo>
          <div class="cd-promo__icon" aria-hidden="true">${icon}</div>
          <div style="min-width:0;">
            <div class="cd-promo__title">${escapeHtml(title)}</div>
            <div class="cd-promo__sub">${escapeHtml(sub)}</div>
          </div>
        </div>
      `;
    }

    _itemHtml(it) {
      const key = escapeHtml(it.key);
      const img = it.featured_image && it.featured_image.url
        ? `${it.featured_image.url}&width=168`
        : (it.image ? `${it.image}&width=168` : '');
      const title = escapeHtml(it.product_title || it.title || '');
      const variant = it.variant_title && it.variant_title !== 'Default Title'
        ? escapeHtml(it.variant_title)
        : '';

      // Discount detection from line_level_discount_allocations
      const original = it.original_line_price ?? (it.original_price * it.quantity);
      const final = it.final_line_price ?? it.line_price ?? (it.price * it.quantity);
      const hasDiscount = original > final;
      const isFree = final === 0 && hasDiscount;

      const priceBlock = isFree
        ? `<div class="cd-item__price-strike">${fmtMoney(it.original_price)}</div>
           <div class="cd-item__price-free">FREE</div>`
        : hasDiscount
          ? `<div class="cd-item__price-strike">${fmtMoney(it.original_price)}</div>
             <div class="cd-item__price">${fmtMoney(it.final_price ?? it.price)}</div>`
          : `<div class="cd-item__price">${fmtMoney(it.final_price ?? it.price)}</div>`;

      const decLabel = it.quantity <= 1 ? 'Remove item' : 'Decrease quantity';

      return `
        <div class="cd-item" data-cd-key="${key}">
          <div class="cd-item__thumb">
            ${img ? `<img src="${escapeHtml(img)}" alt="${title}" loading="lazy" width="168" height="168">` : ''}
          </div>
          <div class="cd-item__meta">
            <div class="cd-item__row1">
              <div style="min-width:0;">
                <span class="cd-item__title">${title}</span>
                ${variant ? `<div class="cd-item__variant">${variant}</div>` : ''}
              </div>
              <div class="cd-item__price-wrap">${priceBlock}</div>
            </div>
            <div class="cd-item__row2">
              <div class="cd-qty" role="group" aria-label="Quantity">
                <button type="button" data-cd-action="dec" data-cd-key="${key}" aria-label="${decLabel}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg>
                </button>
                <div class="cd-qty__value" aria-live="polite">${it.quantity}</div>
                <button type="button" data-cd-action="inc" data-cd-key="${key}" aria-label="Increase quantity">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14"/><path d="M5 12h14"/>
                  </svg>
                </button>
              </div>
              <button type="button" class="cd-item__remove" data-cd-action="remove" data-cd-key="${key}" aria-label="Remove ${title}">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <path d="M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                </svg>
                <span>Remove</span>
              </button>
            </div>
          </div>
        </div>
      `;
    }
  }

  customElements.define('tms-cart-drawer', CartDrawer);
})();
